import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import Paginacion from '../common/Paginacion';

// ── Helpers ────────────────────────────────────────────────────────────────────
// WhatsApp link solo para móviles españoles (6xx / 7xx)
function waLink(num) {
  if (!num) return null;
  const clean = num.replace(/\D/g, '');
  if (!clean) return null;
  const isMobile = /^[67]\d{8}$/.test(clean) || /^34[67]\d{8}$/.test(clean);
  if (!isMobile) return null;
  const withCode = /^34/.test(clean) && clean.length === 11 ? clean : `34${clean}`;
  return `https://wa.me/${withCode}`;
}

function mapsUrl(addr) {
  return addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : null;
}

// Botón de copiar al portapapeles con feedback visual
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} title="Copiar"
      className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors text-xs px-1">
      {copied ? '✓' : '⎘'}
    </button>
  );
}

const TIPOS_EQUIPO = ['BOMBA DE CALOR', 'AIRE ACONDICIONADO', 'CALDERA', 'CONDUCTOS', 'TERMO ELECTRICO', 'CALENTADOR DE GAS', 'OTRO'];

const TIPO_ORDEN_CFG = {
  sat:          { label: 'SAT',          cls: 'bg-orange-100 text-orange-800' },
  instalacion:  { label: 'Instalación',  cls: 'bg-blue-100 text-blue-800' },
  mantenimiento:{ label: 'Mant.',        cls: 'bg-green-100 text-green-800' },
  presupuesto:  { label: 'Presupuesto',  cls: 'bg-purple-100 text-purple-800' },
  garantia:     { label: 'Garantía',     cls: 'bg-gray-100 text-gray-700' },
};

const ESTADO_ORDEN_CFG = {
  pendiente:  { label: 'Pendiente',  cls: 'bg-yellow-100 text-yellow-800' },
  en_ruta:    { label: 'En ruta',    cls: 'bg-blue-100 text-blue-800' },
  en_proceso: { label: 'En proceso', cls: 'bg-amber-100 text-amber-800' },
  cerrada:    { label: 'Cerrada',    cls: 'bg-green-100 text-green-800' },
  cancelada:  { label: 'Cancelada',  cls: 'bg-gray-100 text-gray-500' },
};

function Spinner({ sm }) {
  return (
    <div className={`${sm ? 'w-4 h-4 border-2' : 'w-8 h-8 border-2'} border-gray-200 border-t-red-500 rounded-full animate-spin`} />
  );
}

function Badge({ cfg }) {
  if (!cfg) return null;
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Formulario de cliente (nuevo / editar) ─────────────────────────────────────
function ClienteForm({ cliente, onSave, onCancel }) {
  const isNew = !cliente?.id;
  const [form, setForm] = useState({
    nombre:       cliente?.nombre       || '',
    nombreFiscal: cliente?.nombreFiscal || '',
    email:        cliente?.email        || '',
    telefono:     cliente?.telefono     || '',
    movil:        cliente?.movil        || '',
    nif:          cliente?.nif          || '',
    notas:        cliente?.notas        || '',
    direcciones:  cliente?.direcciones  || [{ id: `dir-${Date.now()}`, etiqueta: 'Principal', direccion: '', cp: '', ciudad: '', provincia: 'Alicante', esPrincipal: true }],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const addDir = () => setForm(p => ({
    ...p,
    direcciones: [...p.direcciones, { id: `dir-${Date.now()}`, etiqueta: '', direccion: '', cp: '', ciudad: '', provincia: '', esPrincipal: false }],
  }));

  const delDir = (idx) => setForm(p => ({ ...p, direcciones: p.direcciones.filter((_, i) => i !== idx) }));

  const setDir = (idx, key, val) => setForm(p => ({
    ...p,
    direcciones: p.direcciones.map((d, i) => i === idx ? { ...d, [key]: val } : d),
  }));

  const handleSubmit = async () => {
    if (!form.nombre.trim()) return setError('El nombre es obligatorio');
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/saveCliente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cliente?.id, ...form }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onSave({ ...form, id: data.id || cliente?.id });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';
  const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <h3 className="font-bold text-gray-900 text-lg">{isNew ? 'Nuevo cliente' : 'Editar cliente'}</h3>
          <button onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Nombre completo <span className="text-red-500">*</span></label>
              <input value={form.nombre} onChange={f('nombre')} className={inputCls} placeholder="Nombre del cliente o empresa" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Nombre fiscal (si distinto)</label>
              <input value={form.nombreFiscal} onChange={f('nombreFiscal')} className={inputCls} placeholder="Razón social para facturas" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={form.email} onChange={f('email')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>NIF / CIF</label>
              <input value={form.nif} onChange={f('nif')} className={inputCls} placeholder="12345678A" />
            </div>
            <div>
              <label className={labelCls}>Teléfono fijo</label>
              <input value={form.telefono} onChange={f('telefono')} className={inputCls} placeholder="965 000 000" />
            </div>
            <div>
              <label className={labelCls}>Móvil</label>
              <input value={form.movil} onChange={f('movil')} className={inputCls} placeholder="666 000 000" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Notas internas</label>
              <textarea value={form.notas} onChange={f('notas')} rows={2} className={`${inputCls} resize-none`} />
            </div>
          </div>

          {/* Direcciones */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={labelCls}>Direcciones</p>
              <button onClick={addDir} className="text-xs text-red-600 font-bold hover:text-red-700">+ Añadir dirección</button>
            </div>
            <div className="space-y-3">
              {form.direcciones.map((d, i) => (
                <div key={d.id || i} className="border border-gray-100 rounded-xl p-3 space-y-2 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <input value={d.etiqueta} onChange={e => setDir(i, 'etiqueta', e.target.value)} className="text-xs font-bold bg-transparent border-none outline-none text-gray-600" placeholder="Etiqueta (ej: Principal)" />
                    {form.direcciones.length > 1 && (
                      <button onClick={() => delDir(i)} className="text-xs text-gray-400 hover:text-red-500">Eliminar</button>
                    )}
                  </div>
                  <input value={d.direccion} onChange={e => setDir(i, 'direccion', e.target.value)} className={`${inputCls} bg-white`} placeholder="Calle, número, piso" />
                  <div className="grid grid-cols-3 gap-2">
                    <input value={d.cp} onChange={e => setDir(i, 'cp', e.target.value)} className={`${inputCls} bg-white`} placeholder="CP" />
                    <input value={d.ciudad} onChange={e => setDir(i, 'ciudad', e.target.value)} className={`${inputCls} bg-white`} placeholder="Ciudad" />
                    <input value={d.provincia} onChange={e => setDir(i, 'provincia', e.target.value)} className={`${inputCls} bg-white`} placeholder="Provincia" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 flex gap-3 justify-end border-t pt-4">
          <button onClick={onCancel} className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
            {saving && <Spinner sm />}
            {isNew ? 'Crear cliente' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Formulario de activo ───────────────────────────────────────────────────────
function ActivoForm({ activo, clienteId, clienteNombre, onSave, onCancel }) {
  const isNew = !activo?.id;
  const [form, setForm] = useState({
    marca:             activo?.marca              || '',
    modelo:            activo?.modelo             || '',
    tipoEquipo:        activo?.tipoEquipo         || 'BOMBA DE CALOR',
    numeroSerie:       activo?.numeroSerie        || '',
    descripcion:       activo?.descripcion        || '',
    fechaInstalacion:  activo?.fechaInstalacion   || '',
    fechaGarantia:     activo?.fechaGarantia      || '',
    sujetoMantenimiento: activo?.sujetoMantenimiento ?? true,
    notas:             activo?.notas              || '',
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';
  const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1';

  const handleSubmit = async () => {
    if (!form.marca.trim()) return setError('La marca es obligatoria');
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/saveActivo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activo?.id, clienteId, clienteNombre, ...form }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onSave({ ...form, clienteId, clienteNombre, id: data.id || activo?.id });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-12">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <h3 className="font-bold text-gray-900">{isNew ? 'Nuevo equipo' : 'Editar equipo'}</h3>
          <button onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipo de equipo</label>
              <select value={form.tipoEquipo} onChange={f('tipoEquipo')} className={inputCls}>
                {TIPOS_EQUIPO.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Marca <span className="text-red-500">*</span></label>
              <input value={form.marca} onChange={f('marca')} className={inputCls} placeholder="Ariston, Daikin…" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Modelo</label>
              <input value={form.modelo} onChange={f('modelo')} className={inputCls} placeholder="NUOS Evo A 80 V" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Número de serie</label>
              <input value={form.numeroSerie} onChange={f('numeroSerie')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fecha instalación</label>
              <input type="date" value={form.fechaInstalacion} onChange={f('fechaInstalacion')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fin garantía</label>
              <input type="date" value={form.fechaGarantia} onChange={f('fechaGarantia')} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Descripción / notas</label>
              <textarea value={form.notas} onChange={f('notas')} rows={2} className={`${inputCls} resize-none`} />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <input type="checkbox" id="sujeto-mant" checked={form.sujetoMantenimiento}
                onChange={e => setForm(p => ({ ...p, sujetoMantenimiento: e.target.checked }))}
                className="w-4 h-4 accent-red-600" />
              <label htmlFor="sujeto-mant" className="text-sm text-gray-700">Sujeto a contrato de mantenimiento</label>
            </div>
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end border-t pt-4">
          <button onClick={onCancel} className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
            {saving && <Spinner sm />}{isNew ? 'Añadir equipo' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Timeline de órdenes (carga async al abrir Resumen) ────────────────────────
function TimelineOrdenes({ cliente }) {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const numericId = /^\d+$/.test(String(cliente.id)) ? cliente.id : null;
    let params;
    if (numericId)            params = `accountId=${numericId}`;
    else if (cliente.referencia) params = `clienteReferencia=${encodeURIComponent(cliente.referencia)}`;
    else if (cliente.stelorderId) params = `stelorderClienteId=${cliente.stelorderId}`;
    else                      params = `clienteNombre=${encodeURIComponent(cliente.nombre || '')}`;

    fetch(`/.netlify/functions/getOrdenes?${params}&limit=8`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => setOrdenes(d.success ? d.ordenes.slice(0, 8) : []))
      .catch(() => setOrdenes([]))
      .finally(() => setLoading(false));
  }, [cliente.id]); // eslint-disable-line

  if (loading) return (
    <div className="flex items-center gap-2 py-1">
      <Spinner sm />
      <span className="text-xs text-gray-400">Cargando historial…</span>
    </div>
  );
  if (!ordenes.length) return <p className="text-xs text-gray-400 italic">Sin órdenes registradas</p>;

  return (
    <div className="overflow-x-auto pb-1 -mx-1 px-1">
      <div className="relative flex items-start min-w-max">
        {/* Línea horizontal conectora */}
        <div className="absolute inset-x-12 top-[7px] h-px bg-gray-200 z-0" />
        {ordenes.map((o, i) => {
          const estadoCfg = ESTADO_ORDEN_CFG[o.estado] || (o.estado ? { label: o.estado, cls: 'bg-gray-100 text-gray-600' } : null);
          return (
            <div key={o.id} className="flex flex-col items-center w-24 flex-shrink-0 relative">
              {/* Punto */}
              <div className={`w-3.5 h-3.5 rounded-full border-2 z-10 flex-shrink-0 ${i === 0 ? 'border-red-500 bg-red-500' : 'border-gray-300 bg-white'}`} />
              {/* Contenido */}
              <div className="mt-2 text-center px-1 w-full">
                <p className="text-[11px] font-bold text-gray-700 truncate">{o.referencia || o.id}</p>
                {estadoCfg && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${estadoCfg.cls} mt-0.5 inline-block leading-tight`}>
                    {estadoCfg.label}
                  </span>
                )}
                <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{o.fechaProgramada || '—'}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Notas CRM con historial ────────────────────────────────────────────────────
function NotasHistorial({ cliente, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [texto, setTexto] = useState('');
  const [saving, setSaving] = useState(false);

  const historial = useMemo(() => {
    const arr = Array.isArray(cliente.notasHistorial) ? [...cliente.notasHistorial] : [];
    return arr.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [cliente.notasHistorial]);

  const handleAdd = async () => {
    if (!texto.trim()) return;
    setSaving(true);
    const nuevaNota = {
      id:     Date.now().toString(),
      texto:  texto.trim(),
      fecha:  new Date().toISOString(),
      autor:  'Admin',
    };
    const nuevaLista = [nuevaNota, ...(cliente.notasHistorial || [])];
    try {
      const r = await fetch('/.netlify/functions/saveCliente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cliente.id, nombre: cliente.nombre, notasHistorial: nuevaLista }),
      });
      const d = await r.json();
      if (d.success || d.id) {
        onUpdate({ ...cliente, notasHistorial: nuevaLista });
        setTexto(''); setShowForm(false);
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (notaId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    const nuevaLista = (cliente.notasHistorial || []).filter(n => n.id !== notaId);
    const r = await fetch('/.netlify/functions/saveCliente', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: cliente.id, nombre: cliente.nombre, notasHistorial: nuevaLista }),
    });
    const d = await r.json();
    if (d.success || d.id) onUpdate({ ...cliente, notasHistorial: nuevaLista });
  };

  const fmtTs = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso.slice(0, 10); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Notas CRM</p>
        <button onClick={() => setShowForm(v => !v)}
          className="text-xs text-red-600 font-semibold hover:text-red-700">
          + Añadir nota
        </button>
      </div>

      {showForm && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-3 space-y-2">
          <textarea
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white resize-none"
            placeholder="Escribe la nota..."
            value={texto}
            onChange={e => setTexto(e.target.value)}
          />
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving || !texto.trim()}
              className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50">
              {saving ? 'Guardando…' : 'Guardar nota'}
            </button>
            <button onClick={() => { setShowForm(false); setTexto(''); }}
              className="px-3 py-1.5 border border-gray-200 text-xs rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Nota legacy */}
      {cliente.notas && historial.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2 mb-2">
          <p className="text-[10px] text-yellow-600 font-bold uppercase mb-1">Nota antigua</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{cliente.notas}</p>
        </div>
      )}

      {/* Historial de notas */}
      {historial.length === 0 && !cliente.notas && (
        <p className="text-xs text-gray-400 italic">Sin notas registradas</p>
      )}
      <div className="space-y-2">
        {historial.map(n => (
          <div key={n.id || n.fecha} className="bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2.5 group">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-yellow-700">{n.autor || 'Admin'}</span>
                <span className="text-[10px] text-gray-400">{fmtTs(n.fecha)}</span>
              </div>
              <button onClick={() => handleDelete(n.id)}
                className="text-gray-200 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.texto}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Modal: Nueva orden desde cliente ──────────────────────────────────────────
function NuevaOrdenModal({ cliente, onClose }) {
  const [form, setForm] = useState({
    descripcion: '',
    fechaProgramada: new Date().toISOString().split('T')[0],
    tipo: 'sat',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  const handleSave = async () => {
    if (!form.descripcion.trim()) { setError('Indica una descripción'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/createWorkOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteNombre:    cliente.nombre,
          clienteEmail:     cliente.email      || '',
          clienteTelefono:  cliente.telefono   || cliente.movil || '',
          clienteDireccion: (cliente.direcciones?.[0]
            ? [cliente.direcciones[0].direccion, cliente.direcciones[0].ciudad].filter(Boolean).join(', ')
            : cliente.direccion) || '',
          descripcion:      form.descripcion,
          fechaProgramada:  form.fechaProgramada,
          tipo:             form.tipo,
        }),
      });
      const d = await r.json();
      if (d.success || d.id) { setOk(true); }
      else setError(d.error || 'Error al crear la orden');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Nueva orden SAT</h3>
            <p className="text-xs text-gray-400">{cliente.nombre}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {ok ? (
          <div className="px-6 py-8 text-center space-y-3">
            <span className="text-4xl">✅</span>
            <p className="text-sm font-bold text-gray-800">Orden creada</p>
            <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl">Cerrar</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Descripción</label>
              <textarea rows={3} className={inp} value={form.descripcion}
                onChange={e => set('descripcion', e.target.value)}
                placeholder="Descripción del problema o trabajo a realizar..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Fecha</label>
                <input type="date" className={inp} value={form.fechaProgramada} onChange={e => set('fechaProgramada', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
                <select className={inp} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                  <option value="sat">SAT</option>
                  <option value="mantenimiento">Mantenimiento</option>
                  <option value="instalacion">Instalación</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-1">
              <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Crear orden
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal: Nuevo presupuesto desde cliente ─────────────────────────────────────
function NuevoPresupuestoModal({ cliente, onClose }) {
  const [form, setForm] = useState({ titulo: '', total: '', descripcion: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  const handleSave = async () => {
    if (!form.titulo.trim()) { setError('Indica un título'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/savePresupuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteNombre: cliente.nombre,
          clienteEmail:  cliente.email || '',
          titulo:        form.titulo,
          descripcion:   form.descripcion,
          total:         form.total ? Number(form.total) : null,
          estado:        'borrador',
          fecha:         new Date().toISOString().split('T')[0],
          validezDias:   30,
          lineas:        [],
        }),
      });
      const d = await r.json();
      if (d.success || d.id) { setOk(true); }
      else setError(d.error || 'Error al crear el presupuesto');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Nuevo presupuesto</h3>
            <p className="text-xs text-gray-400">{cliente.nombre}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {ok ? (
          <div className="px-6 py-8 text-center space-y-3">
            <span className="text-4xl">✅</span>
            <p className="text-sm font-bold text-gray-800">Presupuesto creado en estado borrador</p>
            <p className="text-xs text-gray-400">Edítalo desde la sección Presupuestos</p>
            <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl">Cerrar</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Título del presupuesto</label>
              <input className={inp} value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Ej: Instalación aerotermia Daikin" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Importe estimado (€, opcional)</label>
              <input type="number" className={inp} value={form.total} onChange={e => set('total', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Descripción</label>
              <textarea rows={2} className={inp} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Detalles del trabajo..." />
            </div>
            <div className="flex gap-3 justify-end pt-1">
              <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Crear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Resumen ───────────────────────────────────────────────────────────────
function TabResumen({ cliente, onEdit, onUpdate, facturas = [], loadingFacturas, onVerFacturas }) {
  const [showOrdenModal, setShowOrdenModal] = useState(false);
  const [showPresModal, setShowPresModal]   = useState(false);
  const hasOldDirs = cliente.direcciones?.length > 0;
  const fallbackDir = !hasOldDirs ? cliente.direccion : null;

  const saldo = useMemo(() => {
    const pendientes = facturas.filter(f => f.estado === 'emitida' && f.tipo !== 'abono');
    return pendientes.reduce((s, f) => s + (Number(f.importeTotal ?? f.importe) || 0), 0);
  }, [facturas]);

  return (
    <div className="space-y-5">

      {/* Saldo pendiente — solo si hay facturas */}
      {!loadingFacturas && (saldo > 0 || facturas.length > 0) && (
        <button
          onClick={onVerFacturas}
          className={`w-full text-left rounded-xl px-4 py-3 border flex items-center gap-3 hover:opacity-80 transition-opacity ${saldo > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}
        >
          <span className="text-xl">🧾</span>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${saldo > 0 ? 'text-amber-700' : 'text-gray-700'}`}>
              {saldo > 0 ? `Saldo pendiente: ${fmt(saldo)}` : 'Sin saldo pendiente'}
            </p>
            <p className="text-xs text-gray-400">{facturas.length} factura{facturas.length !== 1 ? 's' : ''} · Ver todas →</p>
          </div>
        </button>
      )}

      {/* Contacto accionable */}
      <div className="bg-gray-50 rounded-xl divide-y divide-gray-100 overflow-hidden">
        {cliente.email && (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-gray-400 text-xs w-4 text-center">✉</span>
            <a href={`mailto:${cliente.email}`}
              className="text-sm text-blue-600 hover:underline flex-1 min-w-0 truncate">
              {cliente.email}
            </a>
            <CopyBtn text={cliente.email} />
          </div>
        )}

        {cliente.telefono && (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-gray-400 text-xs w-4 text-center">☎</span>
            <span className="text-sm text-gray-800 flex-1">{cliente.telefono}</span>
            <CopyBtn text={cliente.telefono} />
            {waLink(cliente.telefono) && (
              <a href={waLink(cliente.telefono)} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 text-xs bg-green-500 hover:bg-green-600 text-white font-semibold px-2 py-0.5 rounded-full transition-colors">
                WhatsApp
              </a>
            )}
          </div>
        )}

        {cliente.movil && cliente.movil !== cliente.telefono && (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-gray-400 text-xs w-4 text-center">📱</span>
            <span className="text-sm text-gray-800 flex-1">{cliente.movil}</span>
            <CopyBtn text={cliente.movil} />
            {waLink(cliente.movil) && (
              <a href={waLink(cliente.movil)} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 text-xs bg-green-500 hover:bg-green-600 text-white font-semibold px-2 py-0.5 rounded-full transition-colors">
                WhatsApp
              </a>
            )}
          </div>
        )}

        {!cliente.email && !cliente.telefono && !cliente.movil && (
          <p className="px-3 py-2.5 text-xs text-gray-400 italic">Sin datos de contacto</p>
        )}
      </div>

      {/* NIF + Nombre fiscal */}
      {(cliente.nif || (cliente.nombreFiscal && cliente.nombreFiscal !== cliente.nombre)) && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {cliente.nif && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">NIF / CIF</p>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-800">{cliente.nif}</span>
                <CopyBtn text={cliente.nif} />
              </div>
            </div>
          )}
          {cliente.nombreFiscal && cliente.nombreFiscal !== cliente.nombre && (
            <div className="col-span-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Nombre fiscal</p>
              <p className="text-sm text-gray-800">{cliente.nombreFiscal}</p>
            </div>
          )}
        </div>
      )}

      {/* Dirección — siempre visible */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Dirección</p>
        {hasOldDirs ? (
          <div className="space-y-2">
            {cliente.direcciones.map((d, i) => {
              const addr = [d.direccion, d.cp, d.ciudad, d.provincia].filter(Boolean).join(', ');
              return (
                <div key={d.id || i} className="bg-gray-50 rounded-xl px-3 py-2.5">
                  {d.etiqueta && <p className="text-xs font-bold text-gray-500 mb-0.5">{d.etiqueta}</p>}
                  <p className="text-sm text-gray-800">{d.direccion}</p>
                  <p className="text-xs text-gray-500">{[d.cp, d.ciudad, d.provincia].filter(Boolean).join(', ')}</p>
                  {mapsUrl(addr) && (
                    <a href={mapsUrl(addr)} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline mt-1 inline-block">
                      Ver en Maps →
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : fallbackDir ? (
          <div className="bg-gray-50 rounded-xl px-3 py-2.5">
            <p className="text-sm text-gray-800">{fallbackDir}</p>
            {mapsUrl(fallbackDir) && (
              <a href={mapsUrl(fallbackDir)} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:underline mt-1 inline-block">
                Ver en Maps →
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic bg-gray-50 rounded-xl px-3 py-2.5">
            Sin dirección registrada
          </p>
        )}
      </div>

      {/* Notas CRM con historial */}
      <NotasHistorial cliente={cliente} onUpdate={onUpdate} />

      {/* Timeline de órdenes */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Historial de órdenes</p>
        <TimelineOrdenes cliente={cliente} />
      </div>

      {/* Acciones rápidas */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setShowOrdenModal(true)}
          className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-orange-50 border border-orange-200 text-orange-700 text-sm font-semibold rounded-xl hover:bg-orange-100 transition-colors">
          🔧 Nueva orden SAT
        </button>
        <button onClick={() => setShowPresModal(true)}
          className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-purple-50 border border-purple-200 text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-100 transition-colors">
          📋 Nuevo presupuesto
        </button>
      </div>

      <button onClick={onEdit} className="flex items-center gap-2 text-sm text-red-600 font-semibold hover:text-red-700">
        ✏️ Editar datos del cliente
      </button>

      {showOrdenModal && <NuevaOrdenModal cliente={cliente} onClose={() => setShowOrdenModal(false)} />}
      {showPresModal  && <NuevoPresupuestoModal cliente={cliente} onClose={() => setShowPresModal(false)} />}
    </div>
  );
}

// ── Modal detalle de orden ─────────────────────────────────────────────────────
function OrdenModal({ orden, onClose }) {
  const tipoCfg   = TIPO_ORDEN_CFG[orden.tipo]    || (orden.tipo    ? { label: orden.tipo,    cls: 'bg-gray-100 text-gray-700' } : null);
  const estadoCfg = ESTADO_ORDEN_CFG[orden.estado] || (orden.estado ? { label: orden.estado,  cls: 'bg-gray-100 text-gray-600' } : null);

  const rawDir = orden['customer-address'] || orden.direccionCliente || null;
  const dirTexto = rawDir
    ? typeof rawDir === 'object'
      ? [rawDir.address, rawDir.city, rawDir.province, rawDir.zip].filter(Boolean).join(', ')
      : String(rawDir)
    : null;

  const pdf         = orden['pdf-path']         || orden.pdfPath       || null;
  const notasInt    = orden['private-comments'] || orden.notas         || null;
  const descripcion = orden.descripcion         || orden.comments      || null;
  const tecnico     = orden.tecnicoNombre       || orden['assignee-name'] || null;
  const fechaAsig   = orden['assigned-date']    || null;

  const campo = (label, val) => val ? (
    <div key={label}>
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-gray-800">{val}</p>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/50 p-4 pt-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-base">{orden.referencia || orden['full-reference']}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge cfg={tipoCfg} />
              <Badge cfg={estadoCfg} />
              {orden.prioridad === 'urgente' && (
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">URGENTE</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0 mt-0.5">✕</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {campo('Fecha programada', orden.fechaProgramada)}
            {campo('Fecha asignada',   fechaAsig)}
            {campo('Técnico',          tecnico)}
            {campo('Cliente',          orden.clienteNombre || orden['customer-name'])}
          </div>

          {dirTexto && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Dirección de servicio</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2">{dirTexto}</p>
            </div>
          )}

          {descripcion && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Descripción</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2 whitespace-pre-wrap">{descripcion}</p>
            </div>
          )}

          {notasInt && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Notas internas</p>
              <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2 whitespace-pre-wrap">{notasInt}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-4 border-t flex items-center gap-3">
          {pdf && (
            <a href={pdf} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors">
              📄 Ver PDF
            </a>
          )}
          <button onClick={onClose}
            className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl ml-auto">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Órdenes ───────────────────────────────────────────────────────────────
function TabOrdenes({ cliente }) {
  const [ordenes,      setOrdenes]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [ordenAbierta, setOrdenAbierta] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let params;
    const numericId = /^\d+$/.test(String(cliente.id)) ? cliente.id : null;
    if (numericId) {
      params = `accountId=${numericId}`;
    } else if (cliente.referencia) {
      params = `clienteReferencia=${encodeURIComponent(cliente.referencia)}`;
    } else if (cliente.stelorderId) {
      params = `stelorderClienteId=${cliente.stelorderId}`;
    } else {
      params = `clienteNombre=${encodeURIComponent(cliente.nombre || '')}`;
    }
    fetch(`/.netlify/functions/getOrdenes?${params}&limit=200`, { signal: controller.signal, headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => setOrdenes(d.success ? d.ordenes : []))
      .catch(err => { if (err.name !== 'AbortError') setOrdenes([]); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [cliente.id]); // eslint-disable-line

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;
  if (!ordenes.length) return <p className="text-sm text-gray-400 text-center py-8">No hay órdenes para este cliente</p>;

  return (
    <>
      {ordenAbierta && <OrdenModal orden={ordenAbierta} onClose={() => setOrdenAbierta(null)} />}
      <div className="space-y-2">
        {ordenes.map(o => {
          const tipoCfg   = TIPO_ORDEN_CFG[o.tipo]    || (o.tipo    ? { label: o.tipo,    cls: 'bg-gray-100 text-gray-700' } : null);
          const estadoCfg = ESTADO_ORDEN_CFG[o.estado] || (o.estado ? { label: o.estado,  cls: 'bg-gray-100 text-gray-600' } : null);
          return (
            <button
              key={o.id}
              onClick={() => setOrdenAbierta(o)}
              className="w-full text-left bg-gray-50 border border-gray-100 hover:border-gray-300 hover:bg-white rounded-xl px-3 py-3 transition-all"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-gray-800">{o.referencia}</p>
                <Badge cfg={tipoCfg} />
                <Badge cfg={estadoCfg} />
                {o.prioridad === 'urgente' && (
                  <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">URGENTE</span>
                )}
                <span className="ml-auto text-gray-300 text-xs">›</span>
              </div>
              {o.descripcion && <p className="text-xs text-gray-500 mt-0.5 truncate">{o.descripcion}</p>}
              <p className="text-xs text-gray-400 mt-0.5">
                {o.fechaProgramada} · {o.tecnicoNombre || o['assignee-name'] || 'Sin técnico'}
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Tab: Activos ───────────────────────────────────────────────────────────────
function TabActivos({ clienteId, clienteNombre }) {
  const [activos,  setActivos]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editActivo, setEditActivo] = useState(null);

  // Clientes StelOrder tienen ID numérico → usar getAssetsByAccount (busca por account-id)
  // Clientes esquema viejo tienen UUID → usar getActivos (busca por clienteId string)
  const isNumericId = /^\d+$/.test(String(clienteId));

  const reload = useCallback(() => {
    setLoading(true);
    const url = isNumericId
      ? `/.netlify/functions/getAssetsByAccount?accountId=${clienteId}`
      : `/.netlify/functions/getActivos?clienteId=${clienteId}`;
    fetch(url, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (isNumericId) {
          // Normalizar campos de StelOrder raw al formato que espera el render
          setActivos((d.assets || []).map(a => ({
            id:               a.id || a._firebaseId,
            marca:            a.brand  || '',
            modelo:           a.model  || '',
            tipoEquipo:       '',
            numeroSerie:      a.serialNumber || a['serial-number'] || '',
            fechaInstalacion: a.warrantyStartDate || a['warranty-start-date'] || '',
            fechaGarantia:    a.warrantyEndDate   || a['warranty-end-date']   || '',
            notas:            a.description || a.name || '',
            sujetoMantenimiento: a['subject-to-maintenance'] || false,
          })));
        } else {
          setActivos(d.success ? d.activos : []);
        }
      })
      .catch(() => setActivos([]))
      .finally(() => setLoading(false));
  }, [clienteId, isNumericId]);

  useEffect(() => { reload(); }, [reload]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este equipo?')) return;
    await fetch(`/.netlify/functions/saveActivo?id=${id}`, { method: 'DELETE' });
    reload();
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <>
      {showForm && (
        <ActivoForm
          activo={editActivo}
          clienteId={clienteId}
          clienteNombre={clienteNombre}
          onSave={() => { setShowForm(false); setEditActivo(null); reload(); }}
          onCancel={() => { setShowForm(false); setEditActivo(null); }}
        />
      )}
      <div className="space-y-3">
        <button onClick={() => { setEditActivo(null); setShowForm(true); }}
          className="w-full py-2 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-red-300 hover:text-red-500 transition-colors">
          + Añadir equipo
        </button>
        {activos.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay equipos registrados</p>}
        {activos.map(a => {
          const enGarantia = a.fechaGarantia && a.fechaGarantia >= today;
          return (
            <div key={a.id} className="border border-gray-100 rounded-xl p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-gray-800">{a.marca} {a.modelo}</p>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{a.tipoEquipo}</span>
                    {enGarantia && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">En garantía</span>}
                    {!enGarantia && a.fechaGarantia && <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">Garantía expirada</span>}
                  </div>
                  {a.numeroSerie && <p className="text-xs text-gray-400 mt-0.5">S/N: {a.numeroSerie}</p>}
                  {a.fechaInstalacion && <p className="text-xs text-gray-400">Instalado: {a.fechaInstalacion}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => { setEditActivo(a); setShowForm(true); }} className="text-xs text-gray-400 hover:text-blue-500">✏️</button>
                  <button onClick={() => handleDelete(a.id)} className="text-xs text-gray-400 hover:text-red-500">🗑️</button>
                </div>
              </div>
              {a.notas && <p className="text-xs text-gray-500 italic">{a.notas}</p>}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Tab: Facturas ──────────────────────────────────────────────────────────────
const fmt = (n) => n != null
  ? Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
  : '—';

const FACTURA_ESTADO_CFG = {
  emitida: { label: 'Pendiente cobro', cls: 'bg-amber-100 text-amber-800' },
  cobrada: { label: 'Cobrada',         cls: 'bg-emerald-100 text-emerald-800' },
  abono:   { label: 'Abono',           cls: 'bg-purple-100 text-purple-800' },
};

function TabFacturas({ facturas, loadingFacturas }) {
  if (loadingFacturas) return <div className="flex justify-center py-8"><Spinner /></div>;

  const pendientes = facturas.filter(f => f.estado === 'emitida' && f.tipo !== 'abono');
  const saldoPendiente = pendientes.reduce((s, f) => s + (Number(f.importeTotal ?? f.importe) || 0), 0);
  const totalFacturado = facturas.filter(f => f.tipo !== 'abono').reduce((s, f) => s + (Number(f.importeTotal ?? f.importe) || 0), 0);

  if (!facturas.length) return (
    <div className="text-center py-12 space-y-2">
      <p className="text-2xl">🧾</p>
      <p className="text-sm font-semibold text-gray-600">Sin facturas registradas</p>
      <p className="text-xs text-gray-400">Las facturas aparecen aquí cuando se sincronizan desde StelOrder</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-xl p-3 border ${saldoPendiente > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Saldo pendiente</p>
          <p className={`text-lg font-bold mt-0.5 ${saldoPendiente > 0 ? 'text-amber-700' : 'text-gray-600'}`}>{fmt(saldoPendiente)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{pendientes.length} factura{pendientes.length !== 1 ? 's' : ''} sin cobrar</p>
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total facturado</p>
          <p className="text-lg font-bold text-gray-800 mt-0.5">{fmt(totalFacturado)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{facturas.filter(f => f.tipo !== 'abono').length} facturas</p>
        </div>
      </div>

      {/* Lista */}
      <div className="space-y-2">
        {facturas.map(f => {
          const cfg = FACTURA_ESTADO_CFG[f.tipo === 'abono' ? 'abono' : (f.estado || 'emitida')] || { label: f.estado, cls: 'bg-gray-100 text-gray-600' };
          const dias = f.estado === 'emitida' && f.fecha
            ? Math.floor((Date.now() - new Date(f.fecha)) / 86400000)
            : null;
          return (
            <div key={f.id} className="border border-gray-100 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-800 flex-1 min-w-0 truncate">
                  {f.referencia || f.id?.slice(-8)}
                </p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.cls}`}>{cfg.label}</span>
                {dias !== null && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${dias > 60 ? 'bg-red-100 text-red-700' : dias > 30 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                    {dias}d
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <p className="text-xs text-gray-400">{f.fecha || '—'}</p>
                <p className="text-xs font-bold text-gray-700 ml-auto">{fmt(f.importeTotal ?? f.importe)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab: Contratos ─────────────────────────────────────────────────────────────
function TabContratos({ clienteId }) {
  const [contratos, setContratos] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    fetch(`/.netlify/functions/getMantenimientos`, { headers: authService.authHeader() })
      .then(r => r.json())
      .then(d => {
        const todos = d.success ? d.mantenimientos : [];
        setContratos(todos.filter(m => String(m.clienteId) === String(clienteId)));
      })
      .catch(() => setContratos([]))
      .finally(() => setLoading(false));
  }, [clienteId]);

  const ESTADO_CFG = {
    firmado_activo:         { label: 'Activo',             cls: 'bg-green-100 text-green-800' },
    pendiente_firma:        { label: 'Pendiente firma',    cls: 'bg-yellow-100 text-yellow-800' },
    contrato_enviado:       { label: 'Enviado',            cls: 'bg-blue-100 text-blue-800' },
    pendiente_factura:      { label: 'Pendiente factura',  cls: 'bg-amber-100 text-amber-800' },
    pendiente_agendar:      { label: 'Pendiente agendar',  cls: 'bg-cyan-100 text-cyan-800' },
    mantenimiento_agendado: { label: 'Agendado',           cls: 'bg-indigo-100 text-indigo-800' },
    pendiente_renovar:      { label: 'Pendiente renovar',  cls: 'bg-purple-100 text-purple-800' },
    sepa_devuelto:          { label: 'SEPA devuelto',      cls: 'bg-red-100 text-red-700' },
    sepa_generado:          { label: 'SEPA generado',      cls: 'bg-orange-100 text-orange-800' },
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;
  if (!contratos.length) return <p className="text-sm text-gray-400 text-center py-8">No hay contratos de mantenimiento</p>;

  return (
    <div className="space-y-2">
      {contratos.map(c => {
        const cfg = ESTADO_CFG[c.estado] || { label: c.estado, cls: 'bg-gray-100 text-gray-600' };
        return (
          <div key={c.id} className="border border-gray-100 rounded-xl px-3 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                  {c.pack && <span className="text-xs text-gray-500 capitalize">{c.pack}</span>}
                  {c.precioAnual && <span className="text-xs font-bold text-gray-700">{c.precioAnual}€/año</span>}
                </div>
                {(c.marca || c.modelo) && <p className="text-xs text-gray-500 mt-0.5">{[c.marca, c.modelo].filter(Boolean).join(' ')}</p>}
                {c.referenciaMandatoSepa && <p className="text-xs text-gray-400 font-mono mt-0.5">SEPA: {c.referenciaMandatoSepa}</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Detalle del cliente (panel lateral) ───────────────────────────────────────
const TABS = [
  { id: 'resumen',     label: 'Resumen' },
  { id: 'ordenes',     label: 'Órdenes' },
  { id: 'incidencias', label: 'Incidencias' },
  { id: 'activos',     label: 'Equipos' },
  { id: 'contratos',   label: 'Contratos' },
  { id: 'facturas',    label: 'Facturas' },
];

const INCIDENCIA_TIPO = {
  897: { label: 'SAT',            cls: 'bg-blue-100 text-blue-700' },
  895: { label: 'Instalación',    cls: 'bg-purple-100 text-purple-700' },
  898: { label: 'Presupuesto',    cls: 'bg-amber-100 text-amber-700' },
  100: { label: 'Mantenimiento',  cls: 'bg-teal-100 text-teal-700' },
};

// ── Tab: Incidencias ──────────────────────────────────────────────────────────
// Incluye las incidencias "sin cita" (createIncident) que antes no aparecían
// en ninguna pantalla de la app hasta pasar por el sync de 30 min.
function TabIncidencias({ cliente }) {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const numericId = /^\d+$/.test(String(cliente.id)) ? cliente.id : (cliente.stelorderId || null);
    if (!numericId) { setIncidencias([]); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    fetch(`/.netlify/functions/getIncidencias?accountId=${numericId}&limit=200`, {
      signal: controller.signal, headers: { ...authService.authHeader() },
    })
      .then(r => r.json())
      .then(d => setIncidencias(d.success ? d.incidencias : []))
      .catch(err => { if (err.name !== 'AbortError') setIncidencias([]); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [cliente.id]); // eslint-disable-line

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;
  if (!incidencias.length) return <p className="text-sm text-gray-400 text-center py-8">No hay incidencias para este cliente</p>;

  return (
    <div className="space-y-2">
      {incidencias.map(inc => {
        const tipoCfg = INCIDENCIA_TIPO[inc.tipoId] || (inc.tipoId ? { label: `Tipo ${inc.tipoId}`, cls: 'bg-gray-100 text-gray-700' } : null);
        return (
          <div key={inc.id} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-3">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-gray-800">{inc.referencia}</p>
              <Badge cfg={tipoCfg} />
              {inc.prioridad && ['HIGH', 'VERYHIGH'].includes(inc.prioridad) && (
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                  {inc.prioridad === 'VERYHIGH' ? 'MUY URGENTE' : 'URGENTE'}
                </span>
              )}
              {inc.origenApp && (
                <span className="text-xs text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded-full">creada en la app</span>
              )}
              {inc.pdfPath && (
                <a href={inc.pdfPath} target="_blank" rel="noopener noreferrer"
                  className="ml-auto text-xs text-blue-600 hover:text-blue-800">PDF ›</a>
              )}
            </div>
            {inc.descripcion && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{inc.descripcion.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 3).join(' · ')}</p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">
              {(inc.fecha || '').slice(0, 10)}{inc.creadoPor ? ` · ${inc.creadoPor}` : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ClienteDetalle({ cliente, onClose, onUpdate }) {
  const [tab,             setTab]             = useState('resumen');
  const [editing,         setEditing]         = useState(false);
  const [facturas,        setFacturas]        = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(true);

  useEffect(() => {
    setLoadingFacturas(true);
    const nombreNorm = (cliente.nombre || '').trim();
    const numId = /^\d+$/.test(String(cliente.id)) ? String(cliente.id)
                : (cliente.stelorderId != null && /^\d+$/.test(String(cliente.stelorderId)) ? String(cliente.stelorderId) : null);

    // Auditoría §6: enlazar por NOMBRE hacía desaparecer el histórico de
    // facturación con un cambio de razón social. Ahora se busca también por
    // ID de StelOrder (account-id / clienteId) y se fusiona — el nombre queda
    // solo como red de seguridad para clientes sin ID numérico.
    const consultas = [];
    if (nombreNorm) consultas.push(getDocs(query(collection(db, 'facturas'), where('clienteNombre', '==', nombreNorm), limit(200))));
    if (numId) {
      consultas.push(getDocs(query(collection(db, 'facturas'), where('account-id', '==', Number(numId)), limit(200))));
      consultas.push(getDocs(query(collection(db, 'facturas'), where('clienteId', '==', numId), limit(200))));
      consultas.push(getDocs(query(collection(db, 'facturas'), where('clienteId', '==', Number(numId)), limit(200))));
    }
    if (consultas.length === 0) { setFacturas([]); setLoadingFacturas(false); return; }

    Promise.allSettled(consultas)
      .then(rs => {
        const map = new Map();
        rs.forEach(r => {
          if (r.status !== 'fulfilled') return;
          r.value.docs.forEach(d => { if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() }); });
        });
        setFacturas([...map.values()].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))));
      })
      .catch(() => setFacturas([]))
      .finally(() => setLoadingFacturas(false));
  }, [cliente.id]); // eslint-disable-line

  return (
    <>
      {editing && (
        <ClienteForm
          cliente={cliente}
          onSave={(updated) => { onUpdate({ ...cliente, ...updated }); setEditing(false); }}
          onCancel={() => setEditing(false)}
        />
      )}
      <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden max-h-[calc(100vh-160px)]">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
            {(cliente.nombre || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-bold text-gray-900 truncate">{cliente.nombre}</p>
              {cliente.referencia && (
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                  {cliente.referencia}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate">
              {[cliente.ciudad, cliente.telefono || cliente.movil].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap flex-shrink-0 border-b-2 transition-colors ${tab === t.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'resumen'     && <TabResumen     cliente={cliente} onEdit={() => setEditing(true)} onUpdate={onUpdate} facturas={facturas} loadingFacturas={loadingFacturas} onVerFacturas={() => setTab('facturas')} />}
          {tab === 'ordenes'     && <TabOrdenes     cliente={cliente} />}
          {tab === 'incidencias' && <TabIncidencias cliente={cliente} />}
          {tab === 'activos'     && <TabActivos     clienteId={cliente.id} clienteNombre={cliente.nombre} />}
          {tab === 'contratos'   && <TabContratos   clienteId={cliente.id} />}
          {tab === 'facturas'    && <TabFacturas    facturas={facturas} loadingFacturas={loadingFacturas} />}
        </div>
      </div>
    </>
  );
}


const POR_PAGINA = 25;

function TituloOrdenable({ label, campo, sort, onSort }) {
  const activo = sort.campo === campo;
  return (
    <button onClick={() => onSort(campo)} className="flex items-center gap-1 hover:text-gray-800">
      {label}
      <span className={`text-[9px] ${activo ? 'text-gray-700' : 'text-gray-300'}`}>
        {activo && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </button>
  );
}

// ── Vista principal ────────────────────────────────────────────────────────────
export default function ClientesView() {
  const [clientes,     setClientes]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadingExtra, setLoadingExtra] = useState(false); // carga en segundo plano
  const [query,        setQuery]        = useState('');
  const [pagina,       setPagina]       = useState(1);
  const [seleccionado, setSeleccionado] = useState(null);
  const [showForm,     setShowForm]     = useState(false);
  const [error,        setError]        = useState('');
  const [sort,         setSort]         = useState({ campo: null, dir: 'asc' });
  const toggleSort = (campo) => {
    setSort(s => (s.campo === campo ? { campo, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }));
  };

  // Carga todos los clientes en batches de 2000 usando la función Netlify (con proyección de campos).
  // El primer batch se muestra de inmediato; los siguientes cargan en segundo plano.
  const cargar = useCallback(async () => {
    setLoading(true);
    setLoadingExtra(false);
    setError('');
    try {
      const BATCH = 2000;
      const all = [];
      let cursor = null;
      let first = true;

      while (true) {
        const url = `/.netlify/functions/getClientes?soloActivos=true&limit=${BATCH}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
        const resp = await fetch(url, { headers: { ...authService.authHeader() } });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!data.success) throw new Error(data.error || 'Error al cargar clientes');

        all.push(...data.clientes);

        if (first) {
          setClientes([...all]);
          setLoading(false);
          first = false;
          if (data.nextCursor) setLoadingExtra(true);
        } else {
          setClientes([...all]);
        }

        cursor = data.nextCursor || null;
        if (!cursor) break;
      }

      // Todo cargado → ordenar por última modificación desc
      all.sort((a, b) => {
        const fa = a._modif || '';
        const fb = b._modif || '';
        if (fb !== fa) return fb.localeCompare(fa);
        return (a._displayName || '').localeCompare(b._displayName || '', 'es', { sensitivity: 'base' });
      });
      setClientes([...all]);
    } catch (e) {
      setError('Error al cargar clientes: ' + e.message);
    } finally {
      setLoading(false);
      setLoadingExtra(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  // Deep-link desde la búsqueda global (⌘K): abre directamente la ficha del
  // cliente indicado en sessionStorage.
  useEffect(() => {
    let objetivo;
    try { objetivo = sessionStorage.getItem('zapata_open_cliente'); } catch { objetivo = null; }
    if (!objetivo || seleccionado) return;
    const match = clientes.find(c => String(c.id) === objetivo || String(c.stelorderId) === objetivo);
    if (match) {
      setSeleccionado(match);
      try { sessionStorage.removeItem('zapata_open_cliente'); } catch { /* noop */ }
      return;
    }
    if (loading) return; // aún cargando lotes — reintenta al llegar más
    // No está en la lista (p. ej. inactivo): tráelo puntualmente.
    fetch(`/.netlify/functions/getClientes?id=${encodeURIComponent(objetivo)}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (d.success && d.cliente) setSeleccionado(d.cliente); })
      .catch(() => {})
      .finally(() => { try { sessionStorage.removeItem('zapata_open_cliente'); } catch { /* noop */ } });
  }, [clientes, loading, seleccionado]);

  // Volver a página 1 cuando cambia la búsqueda o el orden
  useEffect(() => { setPagina(1); }, [query, sort]);

  // Búsqueda en memoria (el servidor ya normaliza todos los campos)
  const clientesFiltrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return clientes;
    const qNum = q.replace(/\s/g, '');
    return clientes.filter(c =>
      (c.nombre       || '').toLowerCase().includes(q) ||
      (c.nombreFiscal || '').toLowerCase().includes(q) ||
      (c.referencia   || '').toLowerCase().includes(q) ||
      (c.email        || '').toLowerCase().includes(q) ||
      (c.nif          || '').toLowerCase().includes(q) ||
      (c.telefono     || '').replace(/\s/g, '').includes(qNum) ||
      (c.movil        || '').replace(/\s/g, '').includes(qNum)
    );
  }, [clientes, query]);

  const clientesOrdenados = useMemo(() => {
    if (!sort.campo) return clientesFiltrados;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...clientesFiltrados].sort((a, b) =>
      String(a[sort.campo] || '').localeCompare(String(b[sort.campo] || ''), 'es', { sensitivity: 'base' }) * dir
    );
  }, [clientesFiltrados, sort]);

  const totalPaginas     = Math.max(1, Math.ceil(clientesFiltrados.length / POR_PAGINA));
  const paginaReal       = Math.min(pagina, totalPaginas);
  const clientesPaginados = clientesOrdenados.slice((paginaReal - 1) * POR_PAGINA, paginaReal * POR_PAGINA);
  const desde            = clientesFiltrados.length === 0 ? 0 : (paginaReal - 1) * POR_PAGINA + 1;
  const hasta            = Math.min(paginaReal * POR_PAGINA, clientesFiltrados.length);

  const handleNuevo = () => {
    cargar();
    setShowForm(false);
  };

  const handleUpdate = (updated) => {
    setClientes(prev => prev.map(c => c.id === updated.id ? updated : c));
    setSeleccionado(updated);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente? Solo se desactivará, no se borrará.')) return;
    await fetch(`/.netlify/functions/saveCliente?id=${id}`, { method: 'DELETE' });
    setSeleccionado(null);
    cargar();
  };

  return (
    <>
      {showForm && (
        <ClienteForm
          onSave={handleNuevo}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className={`flex gap-4 ${seleccionado ? 'h-[calc(100vh-160px)]' : ''}`}>
        {/* ── Lista de clientes ── */}
        <div className={`${seleccionado ? 'w-80 flex-shrink-0 hidden md:flex flex-col' : 'flex-1 flex flex-col'}`}>
          {/* Header */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border mb-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Clientes</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {clientes.length > 0 && `${clientes.length.toLocaleString('es')} cargados`}
                  {loadingExtra && <span className="ml-2 text-blue-500 animate-pulse"> · cargando más…</span>}
                </p>
              </div>
              <button onClick={() => setShowForm(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors">
                + Nuevo
              </button>
            </div>
            <input
              type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Buscar por nombre, teléfono, email, NIF…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
            />
          </div>

          {/* Error */}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm mb-3">{error}</div>}

          {/* Aviso de resultados parciales mientras carga en segundo plano */}
          {loadingExtra && query.trim().length >= 2 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-3 py-2 text-xs mb-2">
              Buscando en {clientes.length.toLocaleString('es')} clientes cargados — puede que falten resultados mientras termina la carga.
            </div>
          )}

          {/* Lista / Tabla */}
          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : clientesFiltrados.length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center space-y-2">
              <p className="text-3xl">👤</p>
              <p className="text-sm font-semibold text-gray-600">{query ? `Sin resultados para "${query}"` : 'No hay clientes'}</p>
              {!query && <button onClick={() => setShowForm(true)} className="text-sm text-red-600 font-bold">+ Crear el primero</button>}
            </div>
          ) : seleccionado ? (
            /* Vista compacta cuando hay detalle abierto */
            <div className="space-y-1 overflow-y-auto">
              {clientesFiltrados.map(c => {
                const isSelected = seleccionado?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSeleccionado(isSelected ? null : c)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${isSelected ? 'bg-red-50 border-red-300' : 'bg-white border-gray-100 hover:border-gray-200'}`}
                  >
                    <p className="text-sm font-semibold text-gray-900 truncate">{c.nombre}</p>
                    <p className="text-xs text-gray-400 truncate">{c.referencia || c.telefono || c.email}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Vista tabla completa con paginación */
            <div className="flex flex-col gap-2">
              <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          <TituloOrdenable label="Ref." campo="referencia" sort={sort} onSort={toggleSort} />
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                          <TituloOrdenable label="Nombre" campo="nombre" sort={sort} onSort={toggleSort} />
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          <TituloOrdenable label="NIF / CIF" campo="nif" sort={sort} onSort={toggleSort} />
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                          <TituloOrdenable label="Teléfono" campo="telefono" sort={sort} onSort={toggleSort} />
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                          <TituloOrdenable label="Email" campo="email" sort={sort} onSort={toggleSort} />
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                          <TituloOrdenable label="Dirección" campo="direccion" sort={sort} onSort={toggleSort} />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {clientesPaginados.map(c => (
                        <tr
                          key={c.id}
                          onClick={() => setSeleccionado(c)}
                          className="hover:bg-red-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span className="text-xs font-mono text-gray-400">{c.referencia || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5 max-w-[200px]">
                            <p className="font-semibold text-gray-900 truncate">{c.nombre}</p>
                            {c.nombreFiscal && c.nombreFiscal !== c.nombre && (
                              <p className="text-xs text-gray-400 truncate">{c.nombreFiscal}</p>
                            )}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{c.nif || '—'}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{c.telefono || c.movil || '—'}</td>
                          <td className="px-4 py-2.5 max-w-[160px]">
                            <span className="text-gray-600 truncate block">{c.email || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5 max-w-[200px]">
                            <span className="text-gray-500 truncate block text-xs">{c.direccion || '—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Pie de paginación ── */}
              <div className="flex items-center justify-between px-1">
                <p className="text-xs text-gray-400">
                  Mostrando {desde}–{hasta} de {clientesFiltrados.length}
                </p>
                <Paginacion page={paginaReal} totalPages={totalPaginas} onChange={setPagina} />
              </div>
            </div>
          )}
        </div>

        {/* ── Panel de detalle ── */}
        {seleccionado && (
          <ClienteDetalle
            cliente={seleccionado}
            onClose={() => setSeleccionado(null)}
            onUpdate={handleUpdate}
          />
        )}

        {/* Mobile: back button when detail open */}
        {seleccionado && (
          <div className="md:hidden fixed bottom-4 left-4 z-40">
            <button onClick={() => setSeleccionado(null)}
              className="flex items-center gap-2 bg-white shadow-lg border px-4 py-2.5 rounded-full text-sm font-bold text-gray-700">
              ← Volver a la lista
            </button>
          </div>
        )}
      </div>
    </>
  );
}
